import React, { useEffect, useState } from "react";
import { Form, Input, Select, Col, Row, Spin, message } from "antd";
import { signUp } from "aws-amplify/auth";
import { LOGGER } from "../../../../helpers/logger";
import { passwordValidator } from "../../../../helpers/passwordPolicy";
import { CM, FL, ML, PA, PFA, SM } from "../../../router/AddRoutesJson";
import { LoadingOutlined } from "@ant-design/icons";
import "../platformAdmin.scss";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { updateUser } from "../../../../api/apiCallManager";
import useProfileDataStore from "../../../../zustand/store/profileDataStore";
import { useRegions } from "../../../../components/hooks/useRegions";
import { showError, showSuccess } from "../../../../helpers/alerts";
import Loader from "../../../../components/spinner/Loader";

const { Option } = Select;

const AddRolesForm = ({ closeModal, editData }) => {
  const queryClient = useQueryClient(); // Get query client instance
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isSiteManager, setIsSiteManager] = useState(false);
  const [isRegion, setIsRegion] = useState(false);
  const {
    data: regionList,
    error: regionError,
    isLoading: regionLoading,
  } = useRegions();
  const userProfile = useProfileDataStore((state) => state.userProfile);
  const mutation = useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      showSuccess("User updated successfully");
      queryClient.invalidateQueries({ queryKey: ["userList"] });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["userProfileDetail"] });
      }, 100);
      closeModal();
    },
  });

  async function handleSignUp(values) {
    const { name, email, password, role, userCategory, siteName, userRegion } =
      values;

    try {
      const { isSignUpComplete, nextStep } = await signUp({
        username: email,
        password: password,
        options: {
          userAttributes: {
            email: email,
            "custom:userName": name,
            "custom:userCategory": userCategory,
            "custom:userPassword": password,
            "custom:siteName": siteName || "",
            "custom:userRegion": userRegion || "",
            "custom:roles": String(role),
            "custom:userEmail": email.toLowerCase(),
            "custom:created_by": userProfile?.userName,
          },
        },
      });
      setIsLoading(false);
      showSuccess("User Created Successfully!");

      LOGGER("Sign-up response:", { isSignUpComplete, nextStep });
      // Invalidate and refetch users query
      queryClient.invalidateQueries({ queryKey: ["userList"] });
      closeModal();
    } catch (error) {
      LOGGER("Error signing up:", error);
      showError(error.message);
      setIsLoading(false);
    }
  }

  const handleUpdateUser = (data) => {
    console.table(data);
    mutation.mutate(data);
  };

  const onFinish = (values) => {
    setIsLoading(true);
    LOGGER("Success:", values);

    if (editData) {
      handleUpdateUser(values);
    } else {
      handleSignUp(values);
    }
  };

  const getRoleOptions = (userCategory) => {
    if (userCategory === "Client_User") {
      return [
        <Option key={CM} value={CM}>
          Client Manager
        </Option>,
        <Option key={SM} value={SM}>
          Site Manager
        </Option>,
      ];
    } else if (userCategory === "Server_User") {
      return [
        // <Option key={PFA} value={PFA}>
        //   Platform Admin
        // </Option>,
        <Option key={PA} value={PA}>
          Project Admin
        </Option>,
        <Option key={ML} value={ML}>
          ML Engineer
        </Option>,
        <Option key={FL} value={FL}>
          FL Engineer
        </Option>,
      ];
    }
    return [];
  };

  useEffect(() => {
    if (editData) {
      if (editData?.userCategory == "Client_User") {
        setIsRegion(true);
      }
      if (editData?.role.includes(SM)) {
        setIsSiteManager(true);
      }
      form.setFieldsValue(editData);
    }
  }, [editData, form]);

  return (
    <>
      {/* {JSON.stringify(editData, null, 4)} */}

      <h1 className="mb-5 font-medium text-[16px]">Create New User</h1>
      <Form
        form={form}
        name="basic"
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label={
                <p className="text-[13px] text-[#626476]">
                  <span className="text-red-500 text-[12px] font-medium">
                    *
                  </span>{" "}
                  Name
                </p>
              }
              rules={[{ required: true, message: "Please enter the name!" }]}
            >
              <Input placeholder="Enter Name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              validateTrigger="onBlur"
              name="email"
              label={
                <p className="text-[13px] text-[#626476]">
                  <span className="text-red-500 text-[12px] font-medium">
                    *
                  </span>{" "}
                  Email
                </p>
              }
              rules={[
                {
                  type: "email",
                  required: true,
                  message: "Please enter a valid email!",
                },
              ]}
            >
              <Input
                disabled={editData}
                autoComplete="off"
                placeholder="Enter Email"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="userCategory"
              label={
                <p className="text-[13px] text-[#626476]">
                  <span className="text-red-500 text-[12px] font-medium">
                    *
                  </span>{" "}
                  User Category
                </p>
              }
              rules={[
                {
                  required: true,
                  message: "Please select the User Category",
                },
              ]}
            >
              <Select
                disabled={editData}
                placeholder="Select User Category"
                onChange={() => {
                  form.setFieldsValue({ role: [] });
                  if (form.getFieldValue("userCategory") == "Client_User") {
                    setIsRegion(true);
                  } else {
                    setIsRegion(false);
                  }
                }}
              >
                <Option value="Client_User">Client User</Option>
                <Option value="Server_User">Server User</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.userCategory !== currentValues.userCategory
              }
            >
              {({ getFieldValue }) => (
                <Form.Item
                  name="role"
                  label={
                    <p className="text-[13px] text-[#626476]">
                      <span className="text-red-500 text-[12px] font-medium">
                        *
                      </span>{" "}
                      Role
                    </p>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please select the role!",
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    onChange={() => {
                      if (getFieldValue("role")?.includes(SM)) {
                        setIsSiteManager(true);
                      }
                      if (!getFieldValue("role")?.includes(SM)) {
                        setIsSiteManager(false);
                        form.setFieldsValue({ siteName: null });
                      }
                    }}
                    placeholder="Select the role"
                    allowClear
                    disabled={!getFieldValue("userCategory")}
                  >
                    {getRoleOptions(getFieldValue("userCategory"))}
                  </Select>
                </Form.Item>
              )}
            </Form.Item>
          </Col>
          {isRegion && (
            <Col span={24}>
              <Form.Item
                name="userRegion"
                label={
                  <p className="text-[13px] text-[#626476]">
                    <span className="text-red-500 text-[12px] font-medium">
                      *
                    </span>{" "}
                    Region
                  </p>
                }
                rules={[
                  {
                    required: true,
                    message: "Please select the User Region",
                  },
                ]}
              >
                <Select disabled={editData} placeholder="Select User Region">
                  {regionList?.map((region, indx) => (
                    <Option key={indx} value={region}>
                      {region}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
          {isSiteManager && (
            <Col span={24}>
              <Form.Item
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.role !== currentValues.role
                }
              >
                {({ getFieldValue }) => (
                  <Form.Item
                    name="siteName"
                    label={
                      <p className="text-[13px] text-[#626476]  hide">
                        <span className="text-red-500 text-[12px] font-medium">
                          *
                        </span>{" "}
                        Site Name
                      </p>
                    }
                    rules={[
                      {
                        required: getFieldValue("role")?.includes(SM),
                        message: "Please enter the site name!",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter Site Name"
                      disabled={
                        !getFieldValue("role")?.includes(SM) || editData
                      }
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
          )}
          {editData ? (
            ""
          ) : (
            <Col span={24}>
              <Form.Item
                name="password"
                label={
                  <p className="text-[13px] text-[#626476]">
                    <span className="text-red-500 text-[12px] font-medium">
                      *
                    </span>{" "}
                    Password
                  </p>
                }
                rules={[{ validator: passwordValidator() }]}
              >
                <Input.Password
                  disabled={editData}
                  autoComplete="new-password"
                  placeholder="Enter Password"
                />
              </Form.Item>
            </Col>
          )}
        </Row>
        <div className="my-2"></div>
        <div className="flex justify-between">
          <button
            type="button"
            onClick={closeModal}
            className="button-common cancel-button me-2"
          >
            Cancel
          </button>
          <button
            disabled={isLoading}
            type="submit"
            className="button-common signin-button ms-2"
          >
            <Loader isLoading={isLoading} />
            {editData ? "Update" : "Submit"}
          </button>
        </div>
      </Form>
    </>
  );
};

export default AddRolesForm;
